"use client";

import { useState, useEffect, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { useHistoryState } from "../hooks/useHistoryState";

export function PhoneLoginForm() {
  const { sendOtp, verifyOtp } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useHistoryState<"phone" | "code">("login-step", "phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const prevStep = useRef(step);
  const phoneRef = useRef<HTMLInputElement>(null);

  // Clear code & error when navigating back to phone step (e.g. browser back)
  useEffect(() => {
    if (prevStep.current === "code" && step === "phone") {
      setCode("");
      setError(null);
    }
    prevStep.current = step;
  }, [step]);

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!phone.trim()) {
      setShake(true);
      phoneRef.current?.focus();
      setTimeout(() => setShake(false), 600);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await sendOtp(phone.trim());
    setLoading(false);

    if (result.ok) {
      setStep("code");
    } else {
      setError(result.error ?? "Failed to send code");
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setError(null);

    const result = await verifyOtp(phone.trim(), code.trim());
    setLoading(false);

    if (!result.ok) {
      setError(result.error ?? "Invalid code");
    }
    // On success, AuthProvider updates state and the parent re-renders
  }

  if (step === "code") {
    return (
      <form onSubmit={handleVerifyOtp} style={formStyle}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={inputStyle}
          autoFocus
        />
        {error && <p style={errorStyle}>{error}</p>}
        <button type="submit" style={verifyBtnStyle} disabled={loading || !code.trim()}>
          {loading ? "Verifying..." : "Verify"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendOtp} style={formStyle}>
      <input
        ref={phoneRef}
        type="tel"
        autoComplete="tel"
        placeholder="+1 (555) 123-4567"
        value={phone}
        onChange={(e) => { setPhone(e.target.value); setShake(false); }}
        style={{ ...inputStyle, ...(shake ? { borderColor: "#ef4444" } : {}) }}
        className={shake ? "shake" : undefined}
        autoFocus
      />
      {error && <p style={errorStyle}>{error}</p>}
      <button type="submit" style={buttonStyle} disabled={loading}>
        {loading ? "Sending..." : "Send Code"}
      </button>
    </form>
  );
}

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  width: "100%",
  maxWidth: "320px",
};

const inputStyle: React.CSSProperties = {
  padding: "0.6rem 0.75rem",
  fontSize: "1rem",
  background: "var(--color-bg-input)",
  color: "var(--color-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--color-border-light)",
  borderRadius: "4px",
  outline: "none",
  textAlign: "center",
  letterSpacing: "0.05em",
};

const buttonStyle: React.CSSProperties = {
  padding: "0.6rem 1rem",
  fontSize: "0.85rem",
  background: "var(--color-btn-primary-bg)",
  color: "var(--color-btn-primary-text)",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontWeight: 600,
};

const verifyBtnStyle: React.CSSProperties = {
  padding: "0.6rem 1rem",
  fontSize: "0.85rem",
  background: "var(--color-bg)",
  color: "var(--color-text)",
  border: "1px solid var(--color-border-light)",
  borderRadius: "4px",
  cursor: "pointer",
  fontWeight: 600,
};

const errorStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "var(--color-text-tertiary)",
  margin: 0,
  textAlign: "center",
};
